/**
 * @class Spectro
 *
 * @property {Number}   id
 * @property {String}   name
 * @property {String}   serialNumber
 * @property {Number}   type
 * @property {String}   typeName
 * @property {Boolean}  isSelected
 * @property {Boolean}  isConnected
 * @property {Boolean}  isCalibrated
 * @property {String}   rgbPreview
 * @property {String}   imagePreview
 * @property {Object}   result
 * @property {Boolean}  dialog
 * @property {Object}   methods         Supported methods by connected spectro
 * @property {Boolean}  spectralCurve
 */
export default class Spectro {
    /**
     * @param {Number} id
     */
    constructor(id) {
        this._id = id;
        this._name = "";
        this._serialNumber = "BB-8";
        this._type = null;
        this._typeName = "";
        this._isSelected = false;
        this._isConnected = false;
        this._isCalibrated = false;
        this._rgbPreview = null;
        this._imagePreview = null;
        this._result = null;
        this._dialog = null;
        this._methods = {};
        this._spectralCurve = false;
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._name = name;
    }

    /**
     * @return {String}
     */
    get name() {
        return this._name;
    }

    /**
     * @param {String} serialNumber
     */
    set serialNumber(serialNumber) {
        this._serialNumber = serialNumber;
    }

    /**
     * @return {String}
     */
    get serialNumber() {
        return this._serialNumber;
    }

    /**
     * @return {Number}
     */
    get type() {
        return this._type;
    }

    /**
     * @param {Number} type
     */
    set type(type) {
        this._type = type;
    }

    /**
     * @param {String} typeName
     */
    set typeName(typeName) {
        this._typeName = typeName;
    }

    /**
     * @return {String}
     */
    get typeName() {
        return this._typeName;
    }

    /**
     * @return {Boolean}
     */
    get isSelected() {
        return this._isSelected;
    }

    /**
     * @param {Boolean} isSelected
     */
    set isSelected(isSelected) {
        this._isSelected = isSelected;
    }

    /**
     * @return {Boolean}
     */
    get isConnected() {
        return this._isConnected;
    }

    /**
     * @param {Boolean} isConnected
     */
    set isConnected(isConnected) {
        this._isConnected = isConnected;
    }

    /**
     * @return {Boolean}
     */
    get isCalibrated() {
        return this._isCalibrated;
    }

    /**
     * @param {Boolean} isCalibrated
     */
    set isCalibrated(isCalibrated) {
        this._isCalibrated = isCalibrated;
    }

    /**
     * @return {String}
     */
    get rgbPreview() {
        return this._rgbPreview;
    }

    /**
     * @param {String} rgbPreview
     */
    set rgbPreview(rgbPreview) {
        this._rgbPreview = rgbPreview;
    }

    /**
     * @return {String}
     */
    get imagePreview() {
        return this._imagePreview;
    }

    /**
     * @param {String} imagePreview
     */
    set imagePreview(imagePreview) {
        this._imagePreview = imagePreview;
    }

    /**
     * @return {Object}
     */
    get result() {
        return this._result;
    }

    /**
     * @param {Object} result
     */
    set result(result) {
        this._result = result;
    }

    /**
     * @return {Object}
     */
    get dialog() {
        return this._dialog;
    }

    /**
     * @param {Object} dialog
     */
    set dialog(dialog) {
        this._dialog = dialog;
    }

    /**
     * @return {Object}
     */
    get methods() {
        return this._methods;
    }

    /**
     * @param {Object}  methods
     */
    set methods(methods) {
        this._methods = methods;
    }

    /**
     * @return {Boolean}
     */
    get spectralCurve() {
        return this._spectralCurve;
    }

    /**
     * @param {Boolean} spectralCurve
     */
    set spectralCurve(spectralCurve) {
        this._spectralCurve = spectralCurve;
    }

    /**
     * @return {String}
     */
    toString() {
        return "Spectro (" + this._id + ")";
    }

    /**
     * @param   {String} method
     * @return  {Boolean}
     */
    validMethod = (method) => {
        return this._methods[method] === true;
    };

    /**
     * Resets spectro to default state.
     *
     * @return  {Object}
     */
    reset = () => {
        let spectro = new Spectro(this._id);
        spectro.name = "";
        spectro.serialNumber = "BB-8";
        spectro.type = null;
        spectro.typeName = "";
        spectro.isSelected = false;
        spectro.isConnected = false;
        spectro.isCalibrated = false;
        spectro.rgbPreview = null;
        spectro.imagePreview = null;
        spectro.result = null;
        spectro.dialog = null;
        spectro.methods = {};
        spectro.spectralCurve = false;

        return spectro;
    };

    /**
     * Clones spectro object.
     *
     * @return  {Object}
     */
    clone = () => {
        let spectro = new Spectro(this._id);
        spectro.name = this._name;
        spectro.serialNumber = this._serialNumber;
        spectro.type = this._type;
        spectro.typeName = this._typeName;
        spectro.isSelected = this._isSelected;
        spectro.isConnected = this._isConnected;
        spectro.isCalibrated = this._isCalibrated;
        spectro.rgbPreview = this._rgbPreview;
        spectro.imagePreview = this.imagePreview;
        spectro.result = this._result;
        spectro.dialog = this._dialog;
        spectro.methods = this._methods;
        spectro.spectralCurve = this._spectralCurve;

        return spectro;
    };

    /**
     * Clones spectro object and updates methods property.
     *
     * @param   {Object}    methods
     * @return  {Object}
     */
    updateMethods = (methods) => {
        let spectro = this.clone();
        spectro.methods = methods;

        return spectro;
    };

    /**
     * Clones spectro object and updates info properties.
     *
     * @param   {String}    name
     * @param   {String}    serialNumber
     * @param   {Number}    type
     * @param   {String}    typeName
     * @param   {Boolean}   spectralCurve
     * @return  {Object}
     */
    updateInfo = (name, serialNumber, type, typeName, spectralCurve) => {
        let spectro = this.clone();
        spectro.name = name;
        spectro.serialNumber = serialNumber;
        spectro.type = type;
        spectro.typeName = typeName;
        spectro.spectralCurve = spectralCurve;

        return spectro;
    };

    /**
     * Clones spectro object and updates isSelected property.
     *
     * @param   {Boolean} isSelected
     * @return  {Object}
     */
    updateSelected = (isSelected) => {
        let spectro = this.clone();
        spectro.isSelected = isSelected;

        return spectro;
    };

    /**
     * Clones spectro object and updates isConnected property.
     *
     * @param   {Boolean} isConnected
     * @return  {Object}
     */
    updateConnected = (isConnected) => {
        let spectro = this.clone();
        spectro.isConnected = isConnected;

        return spectro;
    };

    /**
     * Clones spectro object and updates isCalibrated property.
     *
     * @param   {Boolean} isCalibrated
     * @return  {Object}
     */
    updateCalibrated = (isCalibrated) => {
        let spectro = this.clone();
        spectro.isCalibrated = isCalibrated;

        return spectro;
    };

    /**
     * Clones spectro object and updates image preview property.
     *
     * @param   {String} imagePreview
     * @return  {Object}
     */
    updateImagePreview = (imagePreview) => {
        let spectro = this.clone();
        spectro.imagePreview = imagePreview;

        return spectro;
    };

    /**
     * Clones spectro object and updates preview and result properties.
     *
     * @param   {Object} result
     * @return  {Object}
     */
    updateResult = (result) => {
        let spectro = this.clone();
        spectro.rgbPreview = result === null ? null : result.rgbHex;
        spectro.imagePreview = result === null ? null : result.image;
        spectro.result = result;

        return spectro;
    };

    /**
     * Clones spectro object and updates dialog property.
     *
     * @param   {Object} dialog
     * @return  {Object}
     */
    updateDialog = (dialog) => {
        let spectro = this.clone();
        spectro.dialog = dialog;

        return spectro;
    };
}
